import React from "react";
import { Button } from "./Button";
import styles from "./UpdateBanner.module.scss";

interface UpdateBannerProps {
  /** Whether a newer build is published on the CDN. */
  show: boolean;
  /** The newly published build id, shown as secondary detail. */
  latestVersion?: string;
  /** Reloads the page onto the new build. */
  onReload: () => void;
  /** Keeps the current build for this tab. */
  onDismiss: () => void;
}

/**
 * Non-blocking prompt shown when `useVersionCheck` finds that a newer build has been deployed
 * since this tab loaded.
 *
 * <p>Reloading is the user's call. Auto-reloading would be the "correct" cache-busting move in
 * the abstract, but here it would discard an in-progress quote, so the trade goes the other
 * way: keep serving the old (still working) bundle and let them pick the moment.
 */
export const UpdateBanner: React.FC<UpdateBannerProps> = ({
  show,
  latestVersion,
  onReload,
  onDismiss,
}) => {
  if (!show) return null;

  return (
    <div
      className={styles.updateBanner}
      role="status"
      aria-live="polite"
      data-testid="update-banner"
    >
      <div className={styles.message}>
        <span>A new version of Quick Quote is available.</span>
        {latestVersion && <span className={styles.detail}>Build {latestVersion}</span>}
      </div>
      <div className={styles.actions}>
        <Button onClick={onReload}>Reload</Button>
        <button type="button" className={styles.dismiss} onClick={onDismiss}>
          Not now
        </button>
      </div>
    </div>
  );
};
